import http from 'hub-http/clients/apiClient';
import { CAMPAIGN_ASSOCIATION_NAME } from 'framework-asset-preview-lib/internal/shared/constants/assetPreview';
import { OBJECT_TYPE } from 'framework-asset-preview-lib/internal/shared/constants/objectTypes';
import { fetchBlogPostDetails, fetchContentDetails, fetchLandingPageDetails, fetchSitePageDetails } from 'framework-asset-preview-lib/internal/objectTypes/0-10/api';
import { fetchMarketingEmailDetails, updateMarketingEmail } from 'framework-asset-preview-lib/internal/objectTypes/0-29/api';
import { fetchSmsDetails, updateSMS } from 'framework-asset-preview-lib/internal/objectTypes/0-124/api';
import { updateWhatsApp } from 'framework-asset-preview-lib/internal/objectTypes/0-830/apis/content';
import { fetchSocialDetails } from 'framework-asset-preview-lib/internal/objectTypes/0-71/api';
import { fetchAdCampaignDetails } from 'framework-asset-preview-lib/internal/objectTypes/0-31/api';
import { fetchCtaDetails } from 'framework-asset-preview-lib/internal/objectTypes/0-42/api';
import { fetchWorkflowDetails } from 'framework-asset-preview-lib/internal/objectTypes/0-44/api';
import { fetchFormDetails } from 'framework-asset-preview-lib/internal/objectTypes/0-28/api';
import { updateCampaign } from 'framework-asset-preview-lib/internal/objectTypes/0-35/api';
import { fetchJourneyTrackingPropertyDefinition } from 'framework-asset-preview-lib/internal/objectTypes/0-158/api';
import cachedHttp from './cachedHttp';
const CRM_SEARCH_ENDPOINT = '/crm-search/search';

/**
 * Add campaign information for @objectType that can be associated to a Campaign
 */
function getObjectAssociation(objectType) {
  const campaignAssociationName = CAMPAIGN_ASSOCIATION_NAME[objectType];
  if (!campaignAssociationName) {
    return undefined;
  }
  return {
    [campaignAssociationName]: {
      includeReverseReferences: true,
      allPropertiesFetchMode: 'latest_version',
      includeAllProperties: true
    }
  };
}
function getFilterGroups(objectId, objectType) {
  const objectIdFilter = {
    operator: 'EQ',
    property: 'hs_object_id',
    value: String(objectId)
  };
  const originAssetIdFilter = {
    operator: 'EQ',
    property: 'hs_origin_asset_id',
    value: String(objectId)
  };
  const contentIdFilter = {
    operator: 'EQ',
    property: 'hs_content_id',
    value: String(objectId)
  };

  /**
   * Assets that can also be filtered by hs_origin_asset_id:
   *  - [0-25] LANDING_PAGE: so we can fetch the full Landing page object from CMS BE
   *  - [0-38] SITE_PAGE: so we can fetch the full Site page object from CMS BE
   *  - [0-39] BLOG_POST: so we can fetch the full Blog Post object from CMS BE
   *  - [0-71] SOCIAL_BROADCAST: temporary filter condition until it is fully on the CRM.
   */
  if (objectType === OBJECT_TYPE.LANDING_PAGE || objectType === OBJECT_TYPE.SITE_PAGE || objectType === OBJECT_TYPE.BLOG_POST || objectType === OBJECT_TYPE.SOCIAL_BROADCAST) {
    return [{
      filters: [objectIdFilter]
    }, {
      filters: [originAssetIdFilter]
    }];
  }

  /**
   * Asset that needs to be filtered by hs_content_id:
   *  - [0-10] CONTENT: so we can fetch the full Content object from CMS BE
   */
  if (objectType === OBJECT_TYPE.CONTENT) {
    return [{
      filters: [contentIdFilter]
    }];
  }
  return [{
    filters: [objectIdFilter]
  }];
}

/**
 * The object types in this method use crm-search + a dedicated endpoint so we can enrich
 * the information on each asset preview component.
 */
export function isFetchingFromMultipleSources(objectTypeId) {
  return objectTypeId === OBJECT_TYPE.CONTENT || objectTypeId === OBJECT_TYPE.CTA || objectTypeId === OBJECT_TYPE.FORM || objectTypeId === OBJECT_TYPE.LANDING_PAGE || objectTypeId === OBJECT_TYPE.MARKETING_EMAIL || objectTypeId === OBJECT_TYPE.MARKETING_SMS || objectTypeId === OBJECT_TYPE.AD_CAMPAIGN || objectTypeId === OBJECT_TYPE.SITE_PAGE || objectTypeId === OBJECT_TYPE.BLOG_POST || objectTypeId === OBJECT_TYPE.SOCIAL_BROADCAST || objectTypeId === OBJECT_TYPE.WEB_INTERACTIVE || objectTypeId === OBJECT_TYPE.WORKFLOW || objectTypeId === OBJECT_TYPE.AUTOMATION_JOURNEY;
}
export function fetchNonCrmObject(crmObject) {
  switch (crmObject.objectTypeId) {
    default:
      return Promise.resolve(undefined);
    case OBJECT_TYPE.CONTENT:
      return fetchContentDetails(crmObject);
    case OBJECT_TYPE.CTA:
      return fetchCtaDetails(crmObject);
    case OBJECT_TYPE.FORM:
      return fetchFormDetails(crmObject);
    case OBJECT_TYPE.LANDING_PAGE:
      return fetchLandingPageDetails(crmObject);
    case OBJECT_TYPE.MARKETING_EMAIL:
      return fetchMarketingEmailDetails(crmObject);
    case OBJECT_TYPE.AD_CAMPAIGN:
      return fetchAdCampaignDetails(crmObject);
    case OBJECT_TYPE.SITE_PAGE:
      return fetchSitePageDetails(crmObject);
    case OBJECT_TYPE.BLOG_POST:
      return fetchBlogPostDetails(crmObject);
    case OBJECT_TYPE.SOCIAL_BROADCAST:
      return fetchSocialDetails(crmObject);
    case OBJECT_TYPE.WORKFLOW:
      return fetchWorkflowDetails(crmObject);
    case OBJECT_TYPE.MARKETING_SMS:
      return fetchSmsDetails(crmObject);
    case OBJECT_TYPE.AUTOMATION_JOURNEY:
      return fetchJourneyTrackingPropertyDefinition(crmObject);
  }
}
export function fetchCrmObject(objectId, objectType) {
  const filterGroups = getFilterGroups(objectId, objectType);
  const fetchParams = {
    associationPreviews: getObjectAssociation(objectType),
    count: 1,
    filterGroups,
    objectTypeId: objectType,
    offset: 0,
    query: '',
    requestOptions: {
      allPropertiesFetchMode: 'latest_version',
      includeAllProperties: true
    },
    sorts: []
  };
  return http.post(CRM_SEARCH_ENDPOINT, {
    data: fetchParams
  }).then(crmSearchResponse => {
    if (crmSearchResponse.results.length === 0) {
      return undefined;
    }
    const [crmObject] = crmSearchResponse.results;
    return crmObject;
  });
}
export function fetchCrmObjects(objectType, options) {
  const fetchParams = Object.assign({
    associationPreviews: getObjectAssociation(objectType),
    count: 25,
    filterGroups: [],
    objectTypeId: objectType,
    offset: 0,
    query: '',
    requestOptions: {
      allPropertiesFetchMode: 'latest_version',
      includeAllProperties: true
    },
    sorts: []
  }, options);
  return cachedHttp.post('/crm-search/search', {
    data: fetchParams
  });
}
export function fetchCrmObjectTypeDefinitionsForPortal() {
  return cachedHttp.get(`inbounddb-meta/v1/object-types/for-portal`);
}
export function updateCrmObject({
  // TODO: use this later to save properties
  applicationId: __applicationId,
  changes,
  crmObject,
  crmObjectProperties,
  objectId,
  objectTypeId,
  sourceId,
  isUngatedForEditableUTM
}) {
  switch (objectTypeId) {
    default:
      return http.put(`inbounddb-objects/v1/crm-objects/${objectTypeId}/${objectId}`, {
        data: Object.keys(changes).map(key => ({
          name: key,
          value: changes[key],
          source: 'CRM_UI',
          sourceId
        })),
        query: {
          allPropertiesFetchMode: 'latest_version'
        }
      });
    case OBJECT_TYPE.CAMPAIGN:
      return updateCampaign({
        crmObject,
        changes,
        crmObjectProperties,
        isUngatedForEditableUTM
      });
    case OBJECT_TYPE.MARKETING_SMS:
      return updateSMS({
        crmObject,
        changes
      });
    case OBJECT_TYPE.WHATSAPP:
      return updateWhatsApp({
        crmObject,
        changes
      });
    case OBJECT_TYPE.MARKETING_EMAIL:
      return updateMarketingEmail({
        crmObject,
        changes
      });
  }
}